import { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, Button, Badge, Input, Label, useToast, Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@hiring-intelligence/ui';
import { sourcingApi, SourcedCandidate } from '@/services/api';
import { formatDisplayNumber } from '@hiring-intelligence/shared';

interface CandidateSearchProps {
	hiringRequestId: string;
	candidateSourcing?: Record<string, unknown>;
}

interface ParsedSearch {
	searchNumber: number;
	searchName: string;
	description: string;
	tier?: number;
	parameters: Record<string, unknown>;
}

interface CombinedSearch extends ParsedSearch {
	searchType: 'direct' | 'unconventional';
	originalIndex: number;
}

interface SavedCustomSearch {
	id: string;
	label: string;
	searchType: 'direct' | 'unconventional' | 'custom';
	parameters: Record<string, unknown>;
}

const EMPTY_CUSTOM_PARAMS: Record<string, unknown> = {
	jobTitle: '',
	location: '',
	keywords: '',
	currentPastCompany: '',
	industry: '',
	experienceFrom: '',
	experienceTo: '',
};

function getStorageKey(hiringRequestId: string) {
	return `custom-searches-${hiringRequestId}`;
}

function loadCustomSearches(hiringRequestId: string): SavedCustomSearch[] {
	try {
		const raw = localStorage.getItem(getStorageKey(hiringRequestId));
		return raw ? JSON.parse(raw) : [];
	} catch {
		return [];
	}
}

function saveCustomSearches(hiringRequestId: string, searches: SavedCustomSearch[]) {
	localStorage.setItem(getStorageKey(hiringRequestId), JSON.stringify(searches));
}

export function CandidateSearch({ hiringRequestId, candidateSourcing }: CandidateSearchProps) {
	const { toast } = useToast();

	const [selectedSearch, setSelectedSearch] = useState<string>('');
	const [searchResults, setSearchResults] = useState<any[]>([]);
	const [totalResults, setTotalResults] = useState(0);
	const [isSearching, setIsSearching] = useState(false);
	const [isConfigured, setIsConfigured] = useState(true);
	const [activeSearchInfo, setActiveSearchInfo] = useState<{ searchName: string; description: string } | null>(null);
	const [editedParams, setEditedParams] = useState<Record<string, unknown> | null>(null);
	const [customSearches, setCustomSearches] = useState<SavedCustomSearch[]>([]);
	const [previewCandidate, setPreviewCandidate] = useState<any | null>(null);
	const [renamingId, setRenamingId] = useState<string | null>(null);
	const [renameValue, setRenameValue] = useState('');

	// Load saved custom searches from localStorage on mount; initialize an empty draft
	useEffect(() => {
		setCustomSearches(loadCustomSearches(hiringRequestId));
		setSelectedSearch('draft');
		setEditedParams({ ...EMPTY_CUSTOM_PARAMS });
	}, [hiringRequestId]);

	// Parse the candidate_sourcing module content
	const { directSearches, unconventionalSearches, allSearches } = useMemo(() => {
		if (!candidateSourcing) return { directSearches: [] as CombinedSearch[], unconventionalSearches: [] as CombinedSearch[], allSearches: [] as CombinedSearch[] };

		let content = candidateSourcing as any;

		// Handle rawContent wrapper
		if (content.rawContent && typeof content.rawContent === 'string') {
			try {
				let cleanJson = content.rawContent.trim();
				if (cleanJson.includes('```')) {
					cleanJson = cleanJson.replace(/```json\n?|\n?```/g, '').trim();
				}
				const firstBrace = cleanJson.indexOf('{');
				const lastBrace = cleanJson.lastIndexOf('}');
				if (firstBrace !== -1 && lastBrace !== -1) {
					cleanJson = cleanJson.substring(firstBrace, lastBrace + 1);
				}
				content = JSON.parse(cleanJson);
			} catch {
				return { directSearches: [] as CombinedSearch[], unconventionalSearches: [] as CombinedSearch[], allSearches: [] as CombinedSearch[] };
			}
		}

		const direct = (content.directHuntingSearches || []) as ParsedSearch[];
		const unconventional = (content.unconventionalSearches || []) as ParsedSearch[];

		// Build a combined list with source info, sorted by tier
		const combined = [
			...direct.map((s, i) => ({ ...s, searchType: 'direct' as const, originalIndex: i })),
			...unconventional.map((s, i) => ({ ...s, searchType: 'unconventional' as const, originalIndex: i }))
		].sort((a, b) => (a.tier || 999) - (b.tier || 999));

		// Re-assign sequential tier numbers (1..N) to avoid duplicates
		combined.forEach((s, i) => { s.tier = i + 1; });

		return {
			directSearches: combined.filter(s => s.searchType === 'direct'),
			unconventionalSearches: combined.filter(s => s.searchType === 'unconventional'),
			allSearches: combined
		};
	}, [candidateSourcing]);

	// Transform raw tier parameters for editing:
	// - Remove companySize and industry (not used by SignalHire)
	// - Merge currentCompany/pastCompany into currentPastCompany
	// - Replace totalExperience with experienceFrom / experienceTo
	const transformParams = (raw: Record<string, unknown>): Record<string, unknown> => {
		const params = { ...raw };
		delete params.companySize;
		delete params.industry;

		// Merge currentCompany + pastCompany → currentPastCompany
		if (!params.currentPastCompany) {
			const parts = [params.currentCompany, params.pastCompany].filter(Boolean).map(String);
			params.currentPastCompany = parts.join(', ') || '';
		}
		delete params.currentCompany;
		delete params.pastCompany;

		// Parse totalExperience into experienceFrom / experienceTo
		const exp = String(params.totalExperience || '');
		let from = '';
		let to = '';
		const plusMatch = exp.match(/(\d+)\+/);
		const rangeMatch = exp.match(/(\d+)\s*-\s*(\d+)/);
		const numMatch = exp.match(/(\d+)/);
		if (plusMatch) {
			from = plusMatch[1];
		} else if (rangeMatch) {
			from = rangeMatch[1];
			to = rangeMatch[2];
		} else if (numMatch) {
			from = numMatch[1];
		}
		delete params.totalExperience;
		params.experienceFrom = from;
		params.experienceTo = to;

		return params;
	};

	// Parse a selected value like "direct-2" or "unconventional-0" or "custom-direct-123" or "draft"
	const parseSelected = (value: string) => {
		if (value === 'draft') {
			return { isDraft: true, isCustom: true, searchType: 'custom' as const, index: 0, custom: undefined };
		}
		if (value.startsWith('custom-')) {
			const custom = customSearches.find(c => c.id === value);
			return { isDraft: false, isCustom: true, searchType: custom?.searchType || 'direct' as const, index: 0, custom };
		}
		const [type, idx] = value.split('-');
		return { isDraft: false, isCustom: false, searchType: type as 'direct' | 'unconventional', index: parseInt(idx), custom: undefined };
	};

	// Handler for search selection — initialize editable params from selected search
	const handleSelectSearch = useCallback((value: string) => {
		if (value === 'draft') {
			setSelectedSearch('draft');
			setEditedParams({ ...EMPTY_CUSTOM_PARAMS });
			return;
		}
		setSelectedSearch(value);
		const { isCustom, searchType, index, custom } = parseSelected(value);
		if (isCustom && custom) {
			setEditedParams({ ...custom.parameters });
		} else {
			const search = allSearches.find(s => s.searchType === searchType && s.originalIndex === index);
			if (search) {
				setEditedParams(transformParams(JSON.parse(JSON.stringify(search.parameters || {}))));
			}
		}
	}, [allSearches, customSearches]);

	const updateParam = (key: string, value: string) => {
		setEditedParams(prev => prev ? { ...prev, [key]: value } : { [key]: value });
	};

	const resetParams = () => {
		if (!selectedSearch) return;
		const { isDraft, isCustom, searchType, index, custom } = parseSelected(selectedSearch);
		if (isDraft) {
			setEditedParams({ ...EMPTY_CUSTOM_PARAMS });
			return;
		}
		if (isCustom && custom) {
			setEditedParams({ ...custom.parameters });
		} else {
			const search = allSearches.find(s => s.searchType === searchType && s.originalIndex === index);
			if (search) setEditedParams(transformParams(JSON.parse(JSON.stringify(search.parameters || {}))));
		}
	};

	const saveCustomSearch = () => {
		if (!editedParams || !selectedSearch) return;
		const { isDraft, isCustom, searchType } = parseSelected(selectedSearch);

		// If currently viewing a saved custom search, update it in place
		if (isCustom && !isDraft) {
			const updated = customSearches.map(c =>
				c.id === selectedSearch ? { ...c, parameters: { ...editedParams } } : c
			);
			setCustomSearches(updated);
			saveCustomSearches(hiringRequestId, updated);
			toast({ title: 'Updated', description: `Custom search updated.` });
			return;
		}

		const newSearchType: 'direct' | 'unconventional' | 'custom' = isDraft ? 'custom' : searchType;
		const existingOfType = customSearches.filter(c => c.searchType === newSearchType);
		const nextNum = existingOfType.length + 1;
		const newSearch: SavedCustomSearch = {
			id: `custom-${newSearchType}-${Date.now()}`,
			label: `Custom ${nextNum}`,
			searchType: newSearchType,
			parameters: { ...editedParams },
		};
		const updated = [...customSearches, newSearch];
		setCustomSearches(updated);
		saveCustomSearches(hiringRequestId, updated);
		setSelectedSearch(newSearch.id);
		toast({ title: 'Saved', description: `Custom search saved as "${newSearch.label}".` });
	};

	const deleteCustomSearch = (id: string) => {
		const updated = customSearches.filter(c => c.id !== id);
		setCustomSearches(updated);
		saveCustomSearches(hiringRequestId, updated);
		if (selectedSearch === id) { setSelectedSearch('draft'); setEditedParams({ ...EMPTY_CUSTOM_PARAMS }); }
		toast({ title: 'Deleted', description: 'Custom search removed.' });
	};

	const startRename = (id: string) => {
		const search = customSearches.find(c => c.id === id);
		if (search) {
			setRenamingId(id);
			setRenameValue(search.label);
		}
	};

	const confirmRename = () => {
		if (!renamingId || !renameValue.trim()) return;
		const updated = customSearches.map(c =>
			c.id === renamingId ? { ...c, label: renameValue.trim() } : c
		);
		setCustomSearches(updated);
		saveCustomSearches(hiringRequestId, updated);
		setRenamingId(null);
		setRenameValue('');
		toast({ title: 'Renamed', description: 'Custom search renamed.' });
	};

	// Get saved candidates
	const { data: savedCandidates, refetch: refetchCandidates } = useQuery({
		queryKey: ['sourced-candidates', hiringRequestId],
		queryFn: () => sourcingApi.getCandidates(hiringRequestId)
	});

	// Get credits
	const { data: creditData } = useQuery({
		queryKey: ['signalhire-credits'],
		queryFn: () => sourcingApi.credits()
	});

	// Save candidate mutation
	const saveCandidateMutation = useMutation({
		mutationFn: (candidate: any) => sourcingApi.saveCandidate(hiringRequestId, candidate),
		onSuccess: () => {
			refetchCandidates();
			toast({ title: 'Saved', description: 'Candidate added to pipeline' });
		}
	});

	const handleSearch = async () => {
		if (!selectedSearch) {
			toast({ title: 'Select a search', description: 'Please select a search tier first.', variant: 'destructive' });
			return;
		}

		const { isCustom, searchType, index, custom } = parseSelected(selectedSearch);
		const searchIndex = isCustom ? 0 : index;
		setIsSearching(true);
		setActiveSearchInfo(null);

		try {
			const result = await sourcingApi.searchByTier(hiringRequestId, searchType, searchIndex, editedParams || undefined);
			setSearchResults(result.data.profiles || []);
			setTotalResults(result.data.total || 0);
			if (isCustom) {
				setActiveSearchInfo({ searchName: custom?.label || 'Custom Search', description: 'Saved custom search parameters' });
			} else {
				setActiveSearchInfo(result.searchInfo ? { searchName: result.searchInfo.searchName, description: result.searchInfo.description } : null);
			}
			setIsConfigured(result.isConfigured);

			if (!result.isConfigured) {
				toast({ title: 'Using Mock Data', description: 'Search API not configured. Showing sample results.' });
			}
		} catch (error) {
			toast({
				title: 'Search Failed',
				description: error instanceof Error ? error.message : 'Unknown error',
				variant: 'destructive'
			});
		} finally {
			setIsSearching(false);
		}
	};

	const isSaved = (candidateId: string) => {
		return savedCandidates?.data?.some((c: SourcedCandidate) => c.signalhireId === candidateId);
	};

	const hasModule = candidateSourcing && Object.keys(candidateSourcing).length > 0;
	const hasSearches = allSearches.length > 0;

	// Get description for currently selected search
	const getSelectedDescription = () => {
		if (!selectedSearch) return null;
		const { isDraft, isCustom, searchType, index, custom } = parseSelected(selectedSearch);
		if (isDraft) return 'New custom search (unsaved)';
		if (isCustom) return custom?.label || 'Custom search';
		const search = allSearches.find(s => s.searchType === searchType && s.originalIndex === index);
		return search?.description || null;
	};

	// Short single-line label shown inside the trigger (prevents overflow on smaller screens)
	const getSelectedTriggerLabel = () => {
		if (!selectedSearch) return null;
		const { isDraft, isCustom, searchType, index, custom } = parseSelected(selectedSearch);
		if (isDraft) return '+ New Custom Search';
		if (isCustom) return custom?.label || 'Custom search';
		const search = allSearches.find(s => s.searchType === searchType && s.originalIndex === index);
		if (!search) return null;
		return `Tier ${search.tier} — ${search.searchName}`;
	};

	// Render: no module / no searches fallback
	if (!hasModule) {
		return (
			<Card>
				<CardContent className="py-12 text-center">
					<p className="text-muted-foreground">No candidate sourcing data available.</p>
					<p className="text-sm mt-1 text-muted-foreground">Generate the <strong>Candidate Sourcing</strong> module above to populate search tiers.</p>
				</CardContent>
			</Card>
		);
	}

	if (!hasSearches) {
		return (
			<Card>
				<CardContent className="py-12 text-center">
					<p className="text-muted-foreground">Could not parse search tiers from the module content.</p>
					<p className="text-sm mt-1 text-muted-foreground">Try regenerating the Candidate Sourcing module.</p>
				</CardContent>
			</Card>
		);
	}

	return (
		<div className="flex gap-4 h-[calc(100vh-220px)] min-h-[500px]">
			{/* ── LEFT PANEL: Filters ── */}
			<div className="flex-[2] min-w-0 border rounded-lg overflow-y-auto bg-background">
				<div className="p-4 border-b flex items-start justify-between gap-2">
					<div>
						<h3 className="font-semibold text-sm">Search Candidates</h3>
						<p className="text-xs text-muted-foreground mt-0.5">
							{isConfigured ? 'Select a search tier' : 'Demo mode — API not configured'}
						</p>
					</div>
				</div>

				<div className="p-4 space-y-4">
					{/* Tier selector */}
					<div className="space-y-2">
						<Badge variant="secondary" className="text-xs">{allSearches.length} searches</Badge>
						<Select value={selectedSearch} onValueChange={handleSelectSearch}>
							<SelectTrigger className="text-xs h-10">
								<SelectValue placeholder="Select a search tier...">
									<span className="block truncate text-left">
										{getSelectedTriggerLabel() ?? 'Select a search tier...'}
									</span>
								</SelectValue>
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="draft">
									<div className="flex flex-col">
										<span>+ New Custom Search</span>
										<span className="text-xs text-muted-foreground">Start from a blank search</span>
									</div>
								</SelectItem>
								{directSearches.length > 0 && (
									<SelectGroup>
										<SelectLabel>Direct Hunting (80%)</SelectLabel>
										{directSearches.map((search) => (
											<SelectItem key={`direct-${search.originalIndex}`} value={`direct-${search.originalIndex}`}>
												<div className="flex flex-col">
													<span>Tier {search.tier} — {search.searchName}</span>
													<span className="text-xs text-muted-foreground">{search.description}</span>
												</div>
											</SelectItem>
										))}
									</SelectGroup>
								)}
								{unconventionalSearches.length > 0 && (
									<SelectGroup>
										<SelectLabel>Unconventional (20%)</SelectLabel>
										{unconventionalSearches.map((search) => (
											<SelectItem key={`unconventional-${search.originalIndex}`} value={`unconventional-${search.originalIndex}`}>
												<div className="flex flex-col">
													<span>Tier {search.tier} — {search.searchName}</span>
													<span className="text-xs text-muted-foreground">{search.description}</span>
												</div>
											</SelectItem>
										))}
									</SelectGroup>
								)}
								{customSearches.length > 0 && (
									<SelectGroup>
										<SelectLabel>Custom</SelectLabel>
										{customSearches.map(c => (
											<SelectItem key={c.id} value={c.id}>
												<div className="flex flex-col">
													<span>{c.label}</span>
													<span className="text-xs text-muted-foreground">Saved custom search</span>
												</div>
											</SelectItem>
										))}
									</SelectGroup>
								)}
							</SelectContent>
						</Select>
					</div>

					{/* Editable parameters */}
					{selectedSearch && editedParams && (
						<div className="space-y-3">
							<p className="text-[11px] text-muted-foreground italic">{getSelectedDescription()}</p>
							{renamingId === selectedSearch && (
								<div className="flex items-center gap-2">
									<Input
										className="h-7 text-xs flex-1"
										value={renameValue}
										onChange={e => setRenameValue(e.target.value)}
										onKeyDown={e => { if (e.key === 'Enter') confirmRename(); if (e.key === 'Escape') setRenamingId(null); }}
										autoFocus
										placeholder="Enter new name..."
									/>
									<Button variant="default" size="sm" className="text-xs h-7" onClick={confirmRename}>Save</Button>
									<Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => setRenamingId(null)}>Cancel</Button>
								</div>
							)}
							<div className="space-y-2.5">
								{Object.entries(editedParams).map(([key, val]) => {
									const labelMap: Record<string, string> = {
										jobTitle: 'Title',
										location: 'Location',
										keywords: 'Keywords',
										currentPastCompany: 'Company',
										industry: 'Industry',
										experienceFrom: 'Exp. From (years)',
										experienceTo: 'Exp. To (years)',
									};
									return (
										<div key={key} className="space-y-1">
											<Label className="text-[11px] text-muted-foreground">{labelMap[key] || key}</Label>
											<Input
												className="h-7 text-xs"
												value={Array.isArray(val) ? val.join(', ') : String(val ?? '')}
												onChange={e => updateParam(key, e.target.value)}
											/>
										</div>
									);
								})}
							</div>
							<div className="flex flex-wrap gap-1.5">
								<Button variant="ghost" size="sm" className="text-xs h-7" onClick={saveCustomSearch}>
									{selectedSearch.startsWith('custom-') ? 'Update' : 'Save'}
								</Button>
								{selectedSearch.startsWith('custom-') && (
									<>
										<Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => startRename(selectedSearch)}>
											Rename
										</Button>
										<Button variant="ghost" size="sm" className="text-xs h-7 text-destructive" onClick={() => deleteCustomSearch(selectedSearch)}>
											Delete
										</Button>
									</>
								)}
								<Button variant="ghost" size="sm" className="text-xs h-7" onClick={resetParams}>Reset</Button>
							</div>
						</div>
					)}

					<Button
						className="w-full"
						onClick={handleSearch}
						disabled={isSearching || !selectedSearch}
					>
						{isSearching ? 'Searching...' : 'Search'}
					</Button>
				</div>
			</div>

			{/* ── MIDDLE PANEL: Results ── */}
			<div className="flex-[2] min-w-0 border rounded-lg overflow-y-auto bg-background">
				{searchResults.length > 0 ? (
					<>
						<div className="p-4 border-b sticky top-0 bg-background z-10 flex items-center justify-between">
							<div>
								<h3 className="font-semibold text-sm">Search Results</h3>
								{activeSearchInfo && (
									<p className="text-xs text-muted-foreground">{activeSearchInfo.searchName}</p>
								)}
							</div>
							{totalResults > 0 && (
								<Badge variant="secondary" className="text-xs font-medium">
									{formatDisplayNumber(totalResults)} records
								</Badge>
							)}
						</div>
						<div className="divide-y">
							{searchResults.map((candidate) => (
								<div
									key={candidate.uid}
									className={`p-4 cursor-pointer transition-colors hover:bg-muted/50 ${previewCandidate?.uid === candidate.uid ? 'bg-muted/70 border-l-2 border-l-primary' : ''}`}
									onClick={() => setPreviewCandidate(candidate)}
								>
									<div className="flex items-start justify-between gap-2">
										<div className="flex-1 min-w-0">
											<div className="font-medium text-sm truncate">{candidate.fullName}</div>
											<div className="text-xs text-muted-foreground truncate">
												{candidate.experience?.[0]?.title || 'No Title'} at {candidate.experience?.[0]?.company || 'No Company'}
											</div>
											<div className="text-xs text-muted-foreground mt-0.5">
												📍 {candidate.location}
											</div>
										</div>
										<div className="flex flex-col gap-1 shrink-0">
											{isSaved(candidate.uid) ? (
												<Badge variant="success" className="text-[10px]">Saved</Badge>
											) : (
												<Button
													size="sm"
													variant="outline"
													className="text-xs h-7"
													onClick={(e) => { e.stopPropagation(); saveCandidateMutation.mutate(candidate); }}
													disabled={saveCandidateMutation.isPending}
												>
													Save
												</Button>
											)}
										</div>
									</div>
									{candidate.skills && (
										<div className="flex gap-1 mt-2 flex-wrap">
											{candidate.skills.slice(0, 4).map((skill: string, i: number) => (
												<Badge key={i} variant="outline" className="text-[10px]">
													{skill}
												</Badge>
											))}
											{candidate.skills.length > 4 && (
												<span className="text-[10px] text-muted-foreground">+{candidate.skills.length - 4} more</span>
											)}
										</div>
									)}
								</div>
							))}
						</div>
					</>
				) : activeSearchInfo && !isSearching ? (
					<div className="flex items-center justify-center h-full text-center px-4">
						<div>
							<p className="text-sm font-medium text-muted-foreground">No Results Found</p>
							<p className="text-xs text-muted-foreground mt-1">
								No candidates matched "{activeSearchInfo.searchName}". Try adjusting parameters.
							</p>
						</div>
					</div>
				) : (
					<div className="flex items-center justify-center h-full text-center px-4">
						<div>
							<p className="text-sm text-muted-foreground">Select a search tier and click Search</p>
							<p className="text-xs text-muted-foreground mt-1">Results will appear here</p>
						</div>
					</div>
				)}
			</div>

			{/* ── RIGHT PANEL: Candidate Detail ── */}
			<div className="flex-[3] min-w-0 border rounded-lg overflow-y-auto bg-background">
				{previewCandidate ? (
					<div className="p-4 space-y-4">
						{/* Header */}
						<div>
							<h3 className="font-semibold text-base">{previewCandidate.fullName}</h3>
							<p className="text-xs text-muted-foreground">
								{previewCandidate.experience?.[0]?.title || 'No Title'} at {previewCandidate.experience?.[0]?.company || 'No Company'}
							</p>
							{previewCandidate.location && (
								<p className="text-xs text-muted-foreground mt-0.5">📍 {previewCandidate.location}</p>
							)}
						</div>

						{/* Actions */}
						<div className="flex gap-2">
							{previewCandidate.linkedin_url && (
								<Button
									variant="outline"
									size="sm"
									className="text-xs"
									onClick={() => (globalThis as any).window.open(previewCandidate.linkedin_url, '_blank')}
								>
									LinkedIn
								</Button>
							)}
							{isSaved(previewCandidate.uid) ? (
								<Badge variant="success">Saved</Badge>
							) : (
								<Button
									size="sm"
									className="text-xs"
									onClick={() => saveCandidateMutation.mutate(previewCandidate)}
									disabled={saveCandidateMutation.isPending}
								>
									Save to Pipeline
								</Button>
							)}
						</div>

						{/* Experience */}
						{previewCandidate.experience?.length > 0 && (
							<div className="space-y-2">
								<h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Experience</h4>
								<div className="space-y-2">
									{previewCandidate.experience.map((exp: any, i: number) => (
										<div key={i} className="border-l-2 border-muted pl-3">
											<div className="font-medium text-xs">{exp.title || 'Unknown Title'}</div>
											<div className="text-xs text-muted-foreground">{exp.company || 'Unknown Company'}</div>
											{(exp.start_date || exp.started) && (
												<div className="text-[10px] text-muted-foreground mt-0.5">
													{exp.start_date || exp.started} — {exp.end_date || exp.ended || 'Present'}
												</div>
											)}
										</div>
									))}
								</div>
							</div>
						)}

						{/* Skills */}
						{previewCandidate.skills?.length > 0 && (
							<div className="space-y-2">
								<h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Skills</h4>
								<div className="flex flex-wrap gap-1">
									{previewCandidate.skills.map((skill: string, i: number) => (
										<Badge key={i} variant="outline" className="text-[10px]">
											{skill}
										</Badge>
									))}
								</div>
							</div>
						)}

						{previewCandidate.experience?.length === 0 && previewCandidate.skills?.length === 0 && (
							<p className="text-xs text-muted-foreground py-4 text-center">No experience or skills data available.</p>
						)}
					</div>
				) : (
					<div className="flex items-center justify-center h-full text-center px-4">
						<p className="text-xs text-muted-foreground">Click on a candidate to view details</p>
					</div>
				)}
			</div>
		</div>
	);
}
